import React, { useEffect, useState, useCallback } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { API_URL } from '../config/api';

const PERIODS = [
  { key: 'today', label: "Aujourd'hui" },
  { key: '7d', label: '7 jours' },
  { key: '30d', label: '30 jours' },
  { key: '3m', label: '3 mois' },
  { key: '1y', label: '1 an' },
];

const formatDuration = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

export default function TraficWeb() {
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/track/stats?period=${period}`);
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Erreur récupération stats trafic :', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleExportExcel = () => {
    if (!stats) return;
    const wb = XLSX.utils.book_new();

    const summarySheet = XLSX.utils.aoa_to_sheet([
      ['Métrique', 'Valeur'],
      ['Pages vues', stats.pagesVues],
      ['Sessions uniques', stats.sessionsUniques],
      ['Bounce rate', `${stats.bounceRate}%`],
      ['Durée moy./session', formatDuration(stats.avgSessionDurationSec)],
      ['Pages/session', stats.pagesPerSession],
    ]);
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Résumé');

    const pagesSheet = XLSX.utils.aoa_to_sheet([
      ['Page', 'Vues', 'Pourcentage'],
      ...stats.topPages.map(p => [p.path, p.count, `${p.percent}%`]),
    ]);
    XLSX.utils.book_append_sheet(wb, pagesSheet, 'Pages les plus visitées');

    const roleSheet = XLSX.utils.aoa_to_sheet([
      ['Rôle', 'Vues', 'Pourcentage'],
      ...stats.activityByRole.map(r => [r.role, r.count, `${r.percent}%`]),
    ]);
    XLSX.utils.book_append_sheet(wb, roleSheet, 'Activité par rôle');

    XLSX.writeFile(wb, `trafic_web_${period}.xlsx`);
  };

  const handleExportPDF = () => {
    if (!stats) return;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('🌐 CodeShop225 - Trafic Web', 40, 30);

    doc.autoTable({
      head: [['Métrique', 'Valeur']],
      body: [
        ['Pages vues', stats.pagesVues],
        ['Sessions uniques', stats.sessionsUniques],
        ['Bounce rate', `${stats.bounceRate}%`],
        ['Durée moy./session', formatDuration(stats.avgSessionDurationSec)],
        ['Pages/session', stats.pagesPerSession],
      ],
      startY: 50,
      theme: 'striped',
      headStyles: { fillColor: [108, 93, 211], textColor: 255 },
    });

    doc.autoTable({
      head: [['Page', 'Vues', '%']],
      body: stats.topPages.map(p => [p.path, p.count, `${p.percent}%`]),
      startY: doc.lastAutoTable.finalY + 20,
      theme: 'striped',
      headStyles: { fillColor: [0, 184, 217], textColor: 255 },
    });

    doc.save(`trafic_web_${period}.pdf`);
  };

  return (
    <div style={{ backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <Navbar />
      <main style={{ padding: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
          <div>
            <h1 style={{ margin: 0, color: '#2c3e50' }}>🌐 Trafic web — Tableau de bord</h1>
            <p style={{ margin: '4px 0 0', color: '#7f8c8d' }}>Toutes les pages visitées sur codeshop225.ci</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {PERIODS.map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                style={period === p.key ? periodBtnActiveStyle : periodBtnStyle}
              >
                {p.label}
              </button>
            ))}
            <button onClick={fetchStats} style={refreshBtnStyle}>🔄 Actualiser</button>
            <button onClick={handleExportExcel} style={exportExcelBtnStyle}>📊 Export Excel</button>
            <button onClick={handleExportPDF} style={exportPdfBtnStyle}>📄 Export PDF</button>
          </div>
        </div>

        {loading || !stats ? (
          <p style={{ textAlign: 'center', color: '#7f8c8d' }}>Chargement…</p>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '30px', flexWrap: 'wrap' }}>
              <Kpi label="Pages vues" value={stats.pagesVues.toLocaleString()} color="#6C5DD3" />
              <Kpi label="Sessions uniques" value={stats.sessionsUniques.toLocaleString()} color="#00B8D9" />
              <Kpi label="Bounce rate" value={`${stats.bounceRate}%`} color="#F5A623" />
              <Kpi label="Durée moy./session" value={formatDuration(stats.avgSessionDurationSec)} color="#4CAF50" />
              <Kpi label="Pages/session" value={stats.pagesPerSession} color="#9C27B0" />
            </div>

            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <div style={{ ...panelStyle, flex: '2', minWidth: '320px' }}>
                <h3 style={panelTitleStyle}>📊 Pages les plus visitées</h3>
                {stats.topPages.length === 0 ? (
                  <p style={{ color: '#7f8c8d' }}>Aucune donnée pour cette période.</p>
                ) : (
                  stats.topPages.map((p, i) => (
                    <div key={p.path} style={rowStyle}>
                      <span style={rankStyle}>{i + 1}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500 }}>{p.path}</div>
                        <BarTrack percent={p.percent} color="#00B8D9" />
                      </div>
                      <div style={{ textAlign: 'right', minWidth: '70px' }}>
                        <div style={{ fontWeight: 700, color: '#00B8D9' }}>{p.count}</div>
                        <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>{p.percent}%</div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div style={{ ...panelStyle, flex: '1', minWidth: '280px' }}>
                <h3 style={panelTitleStyle}>👤 Activité par rôle</h3>
                {stats.activityByRole.length === 0 ? (
                  <p style={{ color: '#7f8c8d' }}>Aucune donnée pour cette période.</p>
                ) : (
                  stats.activityByRole.map(r => (
                    <div key={r.role} style={{ marginBottom: '18px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span>{r.role}</span>
                        <span style={{ fontWeight: 600 }}>{r.count} ({r.percent}%)</span>
                      </div>
                      <BarTrack percent={r.percent} color="#6C5DD3" />
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

const Kpi = ({ label, value, color }) => (
  <div style={{ backgroundColor: '#fff', padding: '20px 25px', borderRadius: '10px', minWidth: '160px', boxShadow: '0 4px 10px rgba(0,0,0,0.08)', textAlign: 'center', borderTop: `4px solid ${color}` }}>
    <div style={{ fontSize: '1.6rem', fontWeight: 700, color }}>{value}</div>
    <div style={{ color: '#7f8c8d', marginTop: '6px', fontSize: '0.9rem' }}>{label}</div>
  </div>
);

const BarTrack = ({ percent, color }) => (
  <div style={{ backgroundColor: '#eef1f4', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
    <div style={{ width: `${percent}%`, backgroundColor: color, height: '100%' }} />
  </div>
);

const panelStyle = { backgroundColor: '#fff', borderRadius: '10px', padding: '20px 25px', boxShadow: '0 4px 10px rgba(0,0,0,0.08)' };
const panelTitleStyle = { marginTop: 0, marginBottom: '18px', color: '#2c3e50' };
const rowStyle = { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f0f0f0' };
const rankStyle = { width: '26px', height: '26px', borderRadius: '50%', backgroundColor: '#eef1f4', color: '#34495e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 600 };
const periodBtnStyle = { padding: '8px 16px', borderRadius: '20px', border: '1px solid #ddd', backgroundColor: '#fff', color: '#34495e', cursor: 'pointer' };
const periodBtnActiveStyle = { ...periodBtnStyle, backgroundColor: '#00B8D9', color: '#fff', border: '1px solid #00B8D9' };
const refreshBtnStyle = { padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#eef1f4', color: '#34495e', cursor: 'pointer' };
const exportExcelBtnStyle = { padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#4CAF50', color: '#fff', cursor: 'pointer' };
const exportPdfBtnStyle = { padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#E57373', color: '#fff', cursor: 'pointer' };
